import { type FunctionComponent, type ReactNode, type SyntheticEvent, type MouseEvent } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { useTranslation } from "react-i18next";
import { useFormikContext, getIn } from "formik";
import isEqual from "lodash/isEqual";

import { Button } from "components/Shared/Inputs";
import { confirm } from "components/Shared/ConfirmDialog";
import { StatusChip } from "components/CriticalIncident/components/Shared/StatusChip";

import "./ReportSectionAccordion.scss";

export interface ReportSectionAccordionProps {
  label: string;
  name: string;
  pristineValue: unknown;
  removeAction?: () => void;
  removeHidden?: boolean;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  readonly?: boolean;
  children: ReactNode;
}

export const ReportSectionAccordion: FunctionComponent<ReportSectionAccordionProps> = ({
  label,
  name,
  pristineValue,
  removeAction,
  removeHidden = false,
  expanded,
  onToggle,
  readonly = false,
  children,
}) => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { values, errors } = useFormikContext();

  const sectionErrors = getIn(errors, name);
  const hasErrors = Object.keys(sectionErrors || {}).length > 0;
  const hasData = !isEqual(getIn(values, name), pristineValue);

  const handleAccordionChange = (_event: SyntheticEvent, isExpanded: boolean) => {
    onToggle(isExpanded);
  };

  const handleRemoveClick = async (e: MouseEvent) => {
    e.stopPropagation();

    if (hasData) {
      const confirmed = await confirm({
        title: t('remove_report_section_confirm_title', { label: label?.toLowerCase() }),
        message: t('remove_report_section_confirm_text', { label: label?.toLowerCase() }),
        confirmLabel: t('yes_remove'),
      });
      if (!confirmed) return;
    }

    removeAction?.();
  };

  return translationsReady && (
    <Accordion
      className="report-section-accordion"
      expanded={readonly || expanded}
      onChange={readonly ? undefined : handleAccordionChange}
      elevation={0}
      disableGutters
    >
      <AccordionSummary
        expandIcon={readonly ? undefined : <ExpandMoreIcon />}
        sx={{
          flexDirection: 'row-reverse', // Positions icon to the left
          '& .MuiAccordionSummary-expandIconWrapper': {
            marginRight: 1, // Adds space between icon and text
          },
        }}
      >
        <div className="report-section-accordion__header">
          <div className="report-section-accordion__header-label">
            <span>{label}</span>
            {hasErrors && (
              <StatusChip
                status="error"
                icon={<WarningAmberRounded color="error" style={{ fontSize: '1.25rem' }} />}
                label={t('error_required_fields_missing')}
              />
            )}
          </div>
          {removeAction && !removeHidden && (
            <Button
              variant="text"
              size="small"
              className="report-section-accordion__remove-button"
              onClick={handleRemoveClick}
            >
              {t('remove_group')}
            </Button>
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};
